import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLiveSync } from "@/lib/useLiveSync";

/**
 * useGroceryData — everything the /grocery page needs to reason about
 * the household's shopping list and pantry.
 *
 * Owns:
 *   • the four data arrays (items, vendors, members, staples)
 *   • the vendor-suggestions learning map
 *   • the tab / category / search text state
 *   • live-sync polling
 *
 * Returns pure state + `setters` + one `load` callback. All mutation
 * happens back in the page component (kept close to the toast/UX
 * feedback that goes with it).
 */
export default function useGroceryData() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [members, setMembers] = useState([]);
  const [staples, setStaples] = useState([]);
  const [vendorSuggestions, setVendorSuggestions] = useState({});

  const [tab, setTab] = useState("upcoming");
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (tab !== "all") params.status = tab;
    try {
      const [g, v, m, s, st] = await Promise.all([
        api.get("/grocery", { params }),
        api.get("/vendors"),
        api.get("/household/members"),
        api.get("/grocery/vendor-suggestions"),
        api.get("/staples"),
      ]);
      setItems(g.data);
      setVendors(v.data);
      setMembers(m.data.members || []);
      setVendorSuggestions(s.data?.suggestions || {});
      const spices = (st.data?.spices || []).map((r) => ({ ...r, _bucket: "spice" }));
      const others = (st.data?.others || []).map((r) => ({ ...r, _bucket: r.staple_type || "other" }));
      setStaples([...spices, ...others]);
    } catch (err) {
      console.error("Failed to load grocery:", err);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useLiveSync(load);

  // Filtered items for the currently-active tab + category + search.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (category !== "All" && i.category !== category) return false;
      if (q && !i.name.toLowerCase().includes(q)
           && !(i.note || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, category, search]);

  // Cross-search staple matches — only when the user is actively typing.
  // Ranked so exact matches > prefix > substring.
  const stapleMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const alreadyInList = new Set(
      items.filter((i) => i.status === "upcoming")
        .map((i) => (i.name || "").toLowerCase())
    );
    const scored = [];
    for (const s of staples) {
      const n = (s.name || "").toLowerCase();
      if (!n) continue;
      let score = -1;
      if (n === q) score = 3;
      else if (n.startsWith(q)) score = 2;
      else if (n.includes(q)) score = 1;
      if (score >= 0) scored.push({ s, score, alreadyInList: alreadyInList.has(n) });
    }
    scored.sort((a, b) => b.score - a.score || a.s.name.localeCompare(b.s.name));
    return scored.slice(0, 12);
  }, [staples, items, search]);

  // Online-only vendors (those with a search URL template). Memoised so
  // the OnlineShopIcon menu doesn't re-render every keystroke.
  const onlineVendors = useMemo(
    () => vendors.filter((v) => (v.search_url_template || "").includes("{q}")),
    [vendors]
  );

  const vendorName = useCallback(
    (id) => vendors.find((v) => v.id === id)?.name,
    [vendors]
  );
  const memberFor = useCallback(
    (id) => members.find((m) => m.id === id),
    [members]
  );

  return {
    // raw state
    items, vendors, members, staples, vendorSuggestions,
    tab, setTab,
    category, setCategory,
    search, setSearch,
    // derived
    filtered, stapleMatches, onlineVendors,
    // helpers
    vendorName, memberFor,
    // reloader
    load,
  };
}
